/**
 * AdminOrgsPage — /coach/admin/organizations
 *
 * Org hierarchy tree view with CRUD actions.
 * Shows Org → Group → Sub-group with expand/collapse and member counts.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminOrgAPI, curriculumAPI } from '../../services/api';
import useOrgScope from '../../hooks/useOrgScope';
import CoachLayout from './CoachLayout';

const C = {
  bg: '#1E1E24', surface: '#2a2a32', border: '#3a3a44',
  accent: '#92140C', cream: '#FFF8F0', peach: '#FFCF99',
  text: '#FFF8F0', muted: '#9ca3af', green: '#22c55e', red: '#ef4444',
};

const LEVEL_COLORS = { org: '#7c3aed', group: '#1d4ed8', sub_group: '#0d9488' };
const LEVEL_INDENT = { org: 0, group: 24, sub_group: 48 };
const BILLING_LABELS = {
  flat: 'Flat monthly', per_seat: 'Per seat', tiered: 'Tiered', per_enrollment: 'Per enrollment', free: 'Free',
};

function LevelBadge({ level }) {
  return (
    <span style={{
      background: LEVEL_COLORS[level] || '#374151', color: '#fff', borderRadius: 4,
      padding: '2px 7px', fontSize: 11, fontWeight: 600,
    }}>
      {level}
    </span>
  );
}

// ── Create/Edit Org Modal ──────────────────────────────────────────────────────

function OrgModal({ org, parentId, onClose, onSaved, domainOptions = [], unrestricted = false }) {
  const isEdit = !!org;
  const initialDomains = org?.domain_scope || org?.domain_ids || [];
  const [form, setForm] = useState({
    display_name: org?.display_name || '',
    level: org?.level || 'org',
    parent_org_id: org?.parent_org_id || parentId || '',
    domains: Array.isArray(initialDomains) ? [...initialDomains] : [],
    domainsText: (Array.isArray(initialDomains) ? initialDomains : []).join(', '),
    logo_url: org?.logo_url || '',
    billing_model: org?.billing_model || 'free',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const toggleDomain = (domainId) => {
    setForm((p) => {
      const has = p.domains.includes(domainId);
      const next = has ? p.domains.filter((d) => d !== domainId) : [...p.domains, domainId];
      return { ...p, domains: next, domainsText: next.join(', ') };
    });
  };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const domains = domainOptions.length
        ? form.domains
        : form.domainsText.split(',').map((s) => s.trim()).filter(Boolean);
      const payload = {
        display_name: form.display_name,
        level: form.level,
        // Prefer domain_scope; also send domain_ids for backward compatibility
        domain_scope: domains,
        domain_ids: domains,
        parent_org_id: form.parent_org_id || null,
        logo_url: form.logo_url || null,
        billing_model: form.billing_model || null,
      };
      if (isEdit) {
        await adminOrgAPI.updateOrg(org.org_id, payload);
      } else {
        await adminOrgAPI.createOrg(payload);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const inp = {
    width: '100%', background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 6, padding: '7px 10px', color: C.cream, fontSize: 13, boxSizing: 'border-box',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28, width: 440, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
          <h3 style={{ color: C.cream, margin: 0 }}>{isEdit ? 'Edit' : 'Create'} Organization</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        {err && <div style={{ background: '#7f1d1d', color: C.cream, borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12 }}>{err}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ color: C.muted, fontSize: 12 }}>Display Name *
            <input style={inp} value={form.display_name} onChange={e => setForm(p => ({ ...p, display_name: e.target.value }))} />
          </label>
          {!isEdit && (
            <label style={{ color: C.muted, fontSize: 12 }}>Level *
              <select style={inp} value={form.level} onChange={e => setForm(p => ({ ...p, level: e.target.value }))}>
                <option value="org">org (top-level)</option>
                <option value="group">group</option>
                <option value="sub_group">sub_group</option>
              </select>
            </label>
          )}
          <label style={{ color: C.muted, fontSize: 12 }}>Parent Org ID
            <input style={inp} value={form.parent_org_id} placeholder="Leave blank for top-level org"
              onChange={e => setForm(p => ({ ...p, parent_org_id: e.target.value }))} />
          </label>
          <label style={{ color: C.muted, fontSize: 12 }}>
            Domains in scope
            {domainOptions.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {domainOptions.map((d) => {
                  const id = d.domain_id || d;
                  const label = d.display_name || id;
                  const checked = form.domains.includes(id);
                  return (
                    <label
                      key={id}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: checked ? '#3f1d1d' : C.bg,
                        border: `1px solid ${checked ? C.accent : C.border}`,
                        borderRadius: 6, padding: '6px 10px', color: C.cream, fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleDomain(id)} />
                      {label}
                    </label>
                  );
                })}
                {unrestricted && (
                  <span style={{ color: C.muted, fontSize: 11, alignSelf: 'center' }}>
                    Platform admin — pick from your catalog above
                  </span>
                )}
              </div>
            ) : (
              <input
                style={{ ...inp, marginTop: 4 }}
                value={form.domainsText}
                placeholder="e.g. ai"
                onChange={(e) => setForm((p) => ({
                  ...p,
                  domainsText: e.target.value,
                  domains: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                }))}
              />
            )}
          </label>
          <label style={{ color: C.muted, fontSize: 12 }}>Logo URL
            <input style={inp} value={form.logo_url} placeholder="https://..."
              onChange={e => setForm(p => ({ ...p, logo_url: e.target.value }))} />
          </label>
          <label style={{ color: C.muted, fontSize: 12 }}>Billing Model
            <select style={inp} value={form.billing_model} onChange={e => setForm(p => ({ ...p, billing_model: e.target.value }))}>
              <option value="free">Free</option>
              <option value="flat">Flat monthly</option>
              <option value="per_seat">Per seat</option>
              <option value="tiered">Tiered</option>
              <option value="per_enrollment">Per enrollment</option>
            </select>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: C.border, color: C.cream, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving || !form.display_name}
            style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: C.accent, color: C.cream, cursor: 'pointer', fontWeight: 600 }}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Org Tree Node ──────────────────────────────────────────────────────────────

function OrgNode({ org, allOrgs, onEdit, onDelete, onAddChild, depth = 0 }) {
  const [expanded, setExpanded] = useState(depth < 1);
  const children = allOrgs.filter(o => o.parent_org_id === org.org_id);
  const indent = depth * 24;

  return (
    <div>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', borderRadius: 8, marginBottom: 4,
          background: C.surface, border: `1px solid ${C.border}`,
          marginLeft: indent,
        }}
      >
        {children.length > 0 && (
          <button onClick={() => setExpanded(e => !e)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 14, padding: 0, width: 16 }}>
            {expanded ? '▾' : '▸'}
          </button>
        )}
        {children.length === 0 && <span style={{ width: 16 }} />}

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700, color: C.cream, fontSize: 14 }}>{org.display_name}</span>
            <LevelBadge level={org.level} />
            {org.billing_model && org.billing_model !== 'free' && (
              <span style={{ color: C.peach, fontSize: 11 }}>{BILLING_LABELS[org.billing_model] || org.billing_model}</span>
            )}
          </div>
          <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>
            {org.org_id}
            {(org.domain_scope || org.domain_ids)?.length > 0 && (
              <> · domains: {(org.domain_scope || org.domain_ids).join(', ')}</>
            )}
            {org.parent_org_id && <> · parent: {org.parent_org_id}</>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          {org.level !== 'sub_group' && (
            <button onClick={() => onAddChild(org)} style={{ background: '#14532d', border: 'none', borderRadius: 4, color: C.cream, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}>
              + Child
            </button>
          )}
          <button onClick={() => onEdit(org)} style={{ background: C.border, border: 'none', borderRadius: 4, color: C.cream, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}>
            Edit
          </button>
          <button onClick={() => onDelete(org)} style={{ background: '#7f1d1d', border: 'none', borderRadius: 4, color: C.cream, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}>
            Del
          </button>
        </div>
      </div>

      {expanded && children.map(child => (
        <OrgNode key={child.org_id} org={child} allOrgs={allOrgs}
          onEdit={onEdit} onDelete={onDelete} onAddChild={onAddChild} depth={depth + 1} />
      ))}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function AdminOrgsPage() {
  const { domains: scopeDomains, unrestricted } = useOrgScope();
  const [catalogDomains, setCatalogDomains] = useState([]);
  const domainOptions = useMemo(() => {
    if (scopeDomains?.length) return scopeDomains;
    // Platform admins get unrestricted scope with empty domains — use catalog
    if (unrestricted && catalogDomains.length) return catalogDomains;
    return scopeDomains || [];
  }, [scopeDomains, unrestricted, catalogDomains]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { org?, parentId? }
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!unrestricted) return undefined;
    let cancelled = false;
    curriculumAPI.listDomains()
      .then(({ data }) => {
        if (cancelled) return;
        const rows = (data?.domains || []).map((d) => ({
          domain_id: d.domain_id,
          display_name: d.display_name || d.domain_id,
        }));
        setCatalogDomains(rows);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [unrestricted]);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminOrgAPI.listOrgs();
      setOrgs(res.data.orgs || []);
    } catch {
      setErr('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const roots = orgs.filter(o => !o.parent_org_id);

  const handleDelete = async (org) => {
    if (!window.confirm(`Delete "${org.display_name}"? This will fail if the org has children.`)) return;
    try {
      await adminOrgAPI.deleteOrg(org.org_id);
      fetchOrgs();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <CoachLayout>
      <div style={{ padding: 24, background: C.bg, minHeight: '100vh', color: C.cream }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Organizations</h1>
            <p style={{ margin: '4px 0 0', color: C.muted, fontSize: 13 }}>
              3-level hierarchy: Org → Group → Sub-group · {orgs.length} total
            </p>
          </div>
          <button
            onClick={() => setModal({ org: null, parentId: null })}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: C.accent, color: C.cream, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            + New Org
          </button>
        </div>

        {err && <div style={{ background: '#7f1d1d', color: C.cream, borderRadius: 8, padding: 12, marginBottom: 16 }}>{err}</div>}

        {loading ? (
          <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>Loading…</div>
        ) : roots.length === 0 ? (
          <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>
            No organizations yet. Click "+ New Org" to create the first one.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {roots.map(org => (
              <OrgNode key={org.org_id} org={org} allOrgs={orgs}
                onEdit={o => setModal({ org: o, parentId: null })}
                onDelete={handleDelete}
                onAddChild={parent => setModal({ org: null, parentId: parent.org_id })}
              />
            ))}
          </div>
        )}
      </div>

      {modal !== null && (
        <OrgModal
          org={modal.org}
          parentId={modal.parentId}
          domainOptions={domainOptions}
          unrestricted={unrestricted}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); fetchOrgs(); }}
        />
      )}
    </CoachLayout>
  );
}
